import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";

const BANNER_URL =
  "https://customer-assets.emergentagent.com/job_0394ed20-2daa-42be-8665-34435ea1b0f9/artifacts/rq72gu96_eternal%20now%20banner.png";

const SacredSymbols = () => (
  <div className="flex items-center justify-center space-x-10 mt-12 opacity-80">
    {/* Flower of life-ish */}
    <svg className="w-7 h-7 shimmer" viewBox="0 0 64 64" fill="none" stroke="#ecc97a" strokeWidth="1.2">
      <circle cx="32" cy="32" r="10" />
      <circle cx="22" cy="32" r="10" />
      <circle cx="42" cy="32" r="10" />
      <circle cx="32" cy="22" r="10" />
      <circle cx="32" cy="42" r="10" />
    </svg>
    {/* Spiral */}
    <svg className="w-7 h-7 shimmer" style={{ animationDelay: "0.6s" }} viewBox="0 0 64 64" fill="none" stroke="#ecc97a" strokeWidth="1.2">
      <path d="M32 32 m-2 0 a2 2 0 1 1 4 0 a4 4 0 1 1 -8 0 a8 8 0 1 1 16 0 a16 16 0 1 1 -28 4" />
    </svg>
    {/* Infinity */}
    <svg className="w-9 h-7 shimmer" style={{ animationDelay: "1.2s" }} viewBox="0 0 64 32" fill="none" stroke="#ecc97a" strokeWidth="1.4">
      <path d="M16 16 a8 8 0 1 0 16 0 a8 8 0 1 1 16 0 a8 8 0 1 1 -16 0 a8 8 0 1 0 -16 0 Z" />
    </svg>
    {/* Tree of life */}
    <svg className="w-7 h-7 shimmer" style={{ animationDelay: "1.8s" }} viewBox="0 0 64 64" fill="none" stroke="#ecc97a" strokeWidth="1.2">
      <circle cx="32" cy="32" r="22" />
      <path d="M32 18 v28 M22 28 l20 8 M42 28 l-20 8" />
    </svg>
  </div>
);

const HomePage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const res = await axios.post(`${API}${endpoint}`, formData);

      localStorage.setItem("token", res.data.token);
      setUser(res.data.user);
      setShowAuth(false);
      toast.success(isLogin ? "Welcome back" : "Account created");

      if (!isLogin) navigate("/browse");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/browse`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    toast.success("Logged out");
  };

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 right-0 z-50 p-8">
        <div className="flex items-center space-x-8 text-xs uppercase tracking-[0.25em]">
          {user ? (
            <>
              <button
                data-testid="browse-nav-btn"
                onClick={() => navigate("/browse")}
                className="text-amber-200/80 hover:text-amber-200 transition-colors font-light"
              >
                Browse
              </button>
              <button
                data-testid="library-nav-btn"
                onClick={() => navigate("/library")}
                className="text-amber-200/80 hover:text-amber-200 transition-colors font-light"
              >
                Library
              </button>
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="text-amber-200/80 hover:text-amber-200 transition-colors font-light"
              >
                Logout
              </button>
            </>
          ) : (
            <button
              data-testid="signin-nav-btn"
              onClick={() => setShowAuth(true)}
              className="text-amber-200/80 hover:text-amber-200 transition-colors font-light"
            >
              Sign In
            </button>
          )}
        </div>
      </nav>

      {/* Vertical Text */}
      <div className="hidden md:block fixed left-8 top-1/2 transform -translate-y-1/2 z-10">
        <div className="vertical-text">Quotes From Beyond Time</div>
      </div>

      {/* Hero — full banner */}
      <section className="relative h-screen w-full overflow-hidden">
        <div
          className="absolute inset-0 bg-no-repeat hero-banner"
          style={{ backgroundImage: `url('${BANNER_URL}')` }}
        />
        {/* Vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40" />

        {/* Hero content overlay (just CTAs — banner has the title) */}
        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-20">
          <div className="text-center px-8 fade-in-up">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <Button
                data-testid="get-started-btn"
                onClick={() => (user ? navigate("/browse") : setShowAuth(true))}
                className="gold-btn px-12 py-7 text-xs uppercase font-medium"
              >
                Enter The Eternal
              </Button>

              <Button
                data-testid="view-pricing-home-btn"
                onClick={() => navigate("/pricing")}
                className="gold-outline-btn px-12 py-7 text-xs uppercase font-medium bg-black/40 backdrop-blur-sm hover:bg-amber-200/10"
              >
                See Plans
              </Button>
            </div>
          </div>

          <button
            onClick={scrollToContent}
            className="absolute bottom-6 left-1/2 transform -translate-x-1/2 animate-bounce"
            aria-label="Scroll"
          >
            <ChevronDown className="w-6 h-6 text-amber-200/60" />
          </button>
        </div>
      </section>

      {/* Features — cosmos backdrop */}
      <section className="relative min-h-screen py-32 px-8 cosmos-bg overflow-hidden">
        <div className="relative max-w-6xl mx-auto z-10">
          <div className="text-center mb-24">
            <p className="cinzel text-[10px] tracking-[0.5em] text-amber-200/60 mb-6">
              — Ancient Wisdom Reborn —
            </p>
            <h2 className="cinzel text-3xl md:text-5xl gold-text gold-glow tracking-[0.2em] uppercase">
              What Awaits You
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <FeatureCard
              delay="0s"
              title="Sacred Texts"
              body="Scripture from the Bible, Quran, Vedas, Tao Te Ching, the Sumerian tablets and the Epic of Gilgamesh."
              icon={
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              }
            />
            <FeatureCard
              delay="0.15s"
              title="Higher Consciousness"
              body="Insights from Rumi, Lao Tzu, Louise Hay, Dolores Cannon and modern consciousness pioneers."
              icon={
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6L5.6 18.4" />
                </svg>
              }
            />
            <FeatureCard
              delay="0.3s"
              title="Sound & Vision"
              body="30+ AI voices, 10 languages, binaural soundscapes, branded share cards — your library, your ritual."
              icon={
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              }
            />
          </div>

          <SacredSymbols />

          <div className="text-center mt-20">
            <Button
              data-testid="features-cta-btn"
              onClick={() => (user ? navigate("/browse") : setShowAuth(true))}
              className="gold-btn px-12 py-6 text-xs uppercase font-medium"
            >
              Begin Your Journey
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-amber-200/10 py-12 px-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="script-font text-3xl gold-text">Eternal Now</span>
          <p className="text-[10px] uppercase tracking-[0.4em] text-amber-200/40 cinzel">
            eternalnow.life · Sound & Vision
          </p>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="bg-black border border-amber-200/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="script-font text-5xl text-center gold-text">
              {isLogin ? "Welcome Back" : "Join Eternal Now"}
            </DialogTitle>
            <DialogDescription className="text-center text-amber-200/50 text-xs cinzel tracking-[0.3em]">
              {isLogin ? "Continue your journey" : "Become part of the ritual"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAuth} className="space-y-4 mt-6">
            {!isLogin && (
              <Input
                data-testid="name-input"
                type="text"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-black border-amber-200/20 text-white placeholder:text-gray-600 focus:border-amber-200"
                required={!isLogin}
              />
            )}

            <Input
              data-testid="email-input"
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="bg-black border-amber-200/20 text-white placeholder:text-gray-600 focus:border-amber-200"
              required
            />

            <Input
              data-testid="password-input"
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="bg-black border-amber-200/20 text-white placeholder:text-gray-600 focus:border-amber-200"
              required
            />

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full gold-btn py-6 uppercase tracking-wider text-xs font-medium"
            >
              {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-amber-200/10"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-2 bg-black text-amber-200/40 cinzel">Or</span>
            </div>
          </div>

          <Button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            className="w-full gold-outline-btn py-5 uppercase tracking-wider text-xs font-medium"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </Button>

          <p className="text-center text-amber-200/40 text-xs mt-6 cinzel tracking-[0.2em]">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              data-testid="toggle-auth-btn"
              onClick={() => setIsLogin(!isLogin)}
              className="text-amber-200 hover:text-amber-100 ml-2"
            >
              {isLogin ? "Sign Up" : "Sign In"}
            </button>
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FeatureCard = ({ delay, title, body, icon }) => (
  <div className="text-center space-y-6 fade-in-up" style={{ animationDelay: delay }}>
    <div className="w-20 h-20 mx-auto border border-amber-200/30 flex items-center justify-center text-amber-200/80 relative">
      <div className="absolute inset-0 border border-amber-200/10 rotate-45" />
      {icon}
    </div>
    <h3 className="cinzel text-sm text-amber-100/90 tracking-[0.3em]">{title}</h3>
    <p className="text-xs text-gray-400 leading-relaxed font-light max-w-xs mx-auto">
      {body}
    </p>
  </div>
);

export default HomePage;
